/**
 * The airship's baked light (ADR 0033), lifted from brad-airship: the lamp
 * light over the deck (`airship-lamps.js`) and the static irradiance volumes
 * (`airship-radiance.js`, on the shared engine's `createRadianceVolume`). No
 * point lights, no particles inside the rooms (the ship brief): every lantern,
 * bridge lamp and lit skylight adds warm irradiance to one small 3D texture,
 * scaled at run time by the time of day's `lamps`; the rooms' volumes carry a
 * warm local bounce in RGB and daylight visibility in alpha.
 *
 * Both are built once per ship scene (floor lifetime: `world.ts` owns and
 * disposes the textures).
 */
import * as THREE from 'three';
import type { ShipArea } from './area.ts';

/** A light source for the lamp bake: where its glass is, how strong, and whether it throws up and out only (a skylight). */
export type LampSource = { readonly position: THREE.Vector3; readonly strength: number; readonly down: boolean };

export type VolumeBake = { readonly texture: THREE.Data3DTexture; readonly min: THREE.Vector3; readonly span: THREE.Vector3 };

const volume = (data: Uint8Array, size: readonly [number, number, number], min: readonly [number, number, number], span: readonly [number, number, number]): VolumeBake => {
  const texture = new THREE.Data3DTexture(data, size[0], size[1], size[2]);
  texture.format = THREE.RGBAFormat;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.unpackAlignment = 1;
  texture.needsUpdate = true;
  return { texture, min: new THREE.Vector3(...min), span: new THREE.Vector3(...span) };
};

/**
 * The lamp light over the deck (`bakeLampLight`): every lamp's soft near field and broad pool, warm, into a
 * 128 × 24 × 56 volume over x 5..71, y −3..9, z −15..15. The deck blocks light from above reaching the hull below.
 */
export const bakeLampLight = (lamps: readonly LampSource[]): VolumeBake => {
  const min = [5, -3, -15] as const;
  const max = [71, 9, 15] as const;
  const size = [128, 24, 56] as const;
  const span = [max[0] - min[0], max[1] - min[1], max[2] - min[2]] as const;
  const data = new Uint8Array(size[0] * size[1] * size[2] * 4);
  const warm = [1.0, 0.6, 0.27] as const;
  for (let k = 0; k < size[2]; k += 1) {
    for (let j = 0; j < size[1]; j += 1) {
      for (let i = 0; i < size[0]; i += 1) {
        const x = min[0] + ((i + 0.5) / size[0]) * span[0];
        const y = min[1] + ((j + 0.5) / size[1]) * span[1];
        const z = min[2] + ((k + 0.5) / size[2]) * span[2];
        let e = 0;
        for (const l of lamps) {
          const p = l.position;
          const dx = x - p.x;
          const dy = y - p.y;
          const dz = z - p.z;
          const d2 = dx * dx + dy * dy + dz * dz;
          if (d2 > 42) continue;
          // A skylight throws its light up and out, not down into the room.
          const aim = l.down ? THREE.MathUtils.smoothstep(dy, -0.4, 0.3) : 1;
          const blocked = y < -0.12 && p.y > 0 ? 0.1 : 1;
          // Soft near field (no hot spot on the fitting itself), a broad pool.
          e += l.strength * aim * blocked * (2.4 / (1.4 + d2 * 0.9)) * Math.exp(-d2 / 26);
        }
        const o = ((k * size[1] + j) * size[0] + i) * 4;
        e = e / (1 + e * 0.35);
        for (let c = 0; c < 3; c += 1) data[o + c] = Math.min(255, Math.round(((e * (warm[c] ?? 0)) / 2) * 255));
        data[o + 3] = 255;
      }
    }
  }
  return volume(data, size, min, span);
};

type Pool = { readonly x: number; readonly y: number; readonly z: number; readonly rx: number; readonly ry: number; readonly rz: number; readonly color: readonly [number, number, number] };
type Practical = { readonly x: number; readonly y: number; readonly z: number; readonly rx: number; readonly ry: number; readonly rz: number; readonly strength: number };

/** The rooms' turned extents together, in three's axes. */
const roomsBounds = (area: ShipArea): ShipArea['bounds'] => {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const r of area.rooms) {
    const q = (r.rotation / 90) % 2 === 1;
    const hx = (q ? r.depth : r.width) / 2;
    const hz = (q ? r.width : r.depth) / 2;
    minX = Math.min(minX, r.center.x - hx);
    maxX = Math.max(maxX, r.center.x + hx);
    minZ = Math.min(minZ, r.center.z - hz);
    maxZ = Math.max(maxZ, r.center.z + hz);
  }
  return { minX, maxX, minZ, maxZ };
};

/**
 * A ship scene's static irradiance (`createAirshipRadiance`), 96 × 24 × 64. Below deck: a cool base, warm pools
 * over each incubator, the hatchery's floor and back wall, the farm's beds and potion bench, and daylight shading
 * by the room's practicals. On deck (over `bounds`, the ship and jetty's frame): daylight everywhere, softened in
 * the lee of the bulwarks and under the hull, darker down the hatch mouths.
 */
export const bakeShipRadiance = (area: ShipArea, bounds: THREE.Box3 | null): VolumeBake => {
  const deck = area.id === 'deck';
  // Below deck the volume spans every room drawn (the walked one and its neighbour: the prototype's one interior), not
  // the walked scene's bounds.
  const b = deck || area.rooms.length === 0 ? area.bounds : roomsBounds(area);
  const size = [96, 24, 64] as const;
  const min = deck && bounds !== null ? ([bounds.min.x - 2, bounds.min.y - 2, bounds.min.z - 2] as const) : ([b.minX - 2, -1, b.minZ - 2] as const);
  const extent = bounds?.getSize(new THREE.Vector3());
  const span = deck && extent !== undefined ? ([extent.x + 4, extent.y + 4, extent.z + 4] as const) : ([b.maxX - b.minX + 4, 7, b.maxZ - b.minZ + 4] as const);
  const data = new Uint8Array(size[0] * size[1] * size[2] * 4);
  const pools: Pool[] = [];
  const practicals: Practical[] = [];
  for (const room of area.rooms) {
    const toWorld = (x: number, z: number): { x: number; z: number } => {
      let px = x;
      let pz = z;
      for (let q = 0; q < room.rotation / 90; q += 1) [px, pz] = [-pz, px];
      return { x: px + room.center.x, z: pz + room.center.z };
    };
    const kinds = (k: string): { x: number; z: number; h: number }[] => room.localInteractions.filter((i) => i.kind === k).map((i) => ({ ...toWorld(i.x, i.z), h: i.h }));
    if (room.type === 'hatchery') {
      for (const i of kinds('incubator')) {
        practicals.push({ x: i.x, z: i.z, y: i.h || 1.25, rx: 1.25, rz: 1.25, ry: 0.65, strength: 0.44 });
        practicals.push({ x: i.x, z: i.z - 0.35, y: (i.h || 1.25) + 1, rx: 0.75, rz: 0.8, ry: 0.4, strength: 0.42 });
        pools.push({ x: i.x, y: 1.55, z: i.z, rx: 1.5, rz: 1.35, ry: 1.6, color: [0.4, 0.25, 0.11] });
      }
      pools.push({ x: room.center.x - 0.5, y: 0.15, z: room.center.z + 0.25, rx: 2.8, rz: 2.45, ry: 1.1, color: [0.86, 0.6, 0.32] });
      pools.push({ x: room.center.x, y: 2.65, z: room.center.z - room.depth / 2 + 0.7, rx: 5.2, rz: 1.4, ry: 1.2, color: [0.32, 0.23, 0.14] });
    } else if (room.type === 'farm') {
      pools.push({ x: room.center.x + 1, y: 0.2, z: room.center.z - 0.65, rx: 2.6, rz: 3, ry: 1.2, color: [0.76, 0.62, 0.34] });
      for (const i of kinds('potion-bench')) {
        practicals.push({ x: i.x, z: i.z, y: 1.45, rx: 1.8, rz: 1.2, ry: 0.6, strength: 0.6 });
        pools.push({ x: i.x, y: 0.7, z: i.z, rx: 1.6, rz: 1.2, ry: 1.1, color: [0.55, 0.35, 0.15] });
      }
      pools.push({ x: room.center.x, y: 2.4, z: room.center.z - room.depth / 2 + 0.6, rx: 5, rz: 1.4, ry: 1.3, color: [0.29, 0.25, 0.15] });
    }
    if (!deck) for (const side of [-1, 1]) pools.push({ x: room.center.x + side * (room.width / 2 - 0.5), y: 1.15, z: room.center.z + 0.3, rx: 1.2, rz: 3.8, ry: 1.4, color: [0.2, 0.16, 0.11] });
  }
  const sq = (v: number): number => v * v;
  for (let z = 0; z < size[2]; z += 1) {
    for (let y = 0; y < size[1]; y += 1) {
      for (let x = 0; x < size[0]; x += 1) {
        const px = min[0] + ((x + 0.5) / size[0]) * span[0];
        const py = min[1] + ((y + 0.5) / size[1]) * span[1];
        const pz = min[2] + ((z + 0.5) / size[2]) * span[2];
        let rgb: [number, number, number];
        let daylight: number;
        if (deck) {
          // Open deck: daylight everywhere, softened in the lee of the bulwarks and under the hull, darker down the hatch mouths.
          const hull = THREE.MathUtils.smoothstep(py, -2.5, -0.05);
          const bulwark = THREE.MathUtils.smoothstep(Math.abs(pz), 6.9, 8.1) * (1 - THREE.MathUtils.smoothstep(py, 0.2, 1.3)) * (1 - THREE.MathUtils.smoothstep(px, 53.5, 55));
          daylight = (1 - 0.22 * bulwark) * (0.8 + 0.2 * hull);
          for (const portal of area.portals) daylight -= 0.35 * Math.exp(-(sq((px - portal.x) / 0.6) + sq((pz - portal.z) / 0.6) + sq((py + 0.1) / 0.3)));
          rgb = [0.05 + 0.04 * bulwark, 0.04 + 0.03 * bulwark, 0.03 + 0.01 * bulwark];
        } else {
          rgb = [0.055, 0.07, 0.11];
          let poolAmount = 0;
          for (const p of pools) {
            const a = Math.exp(-(sq((px - p.x) / p.rx) + sq((pz - p.z) / p.rz) + sq((py - p.y) / p.ry)));
            for (let c = 0; c < 3; c += 1) rgb[c] = (rgb[c] ?? 0) + p.color[c as 0 | 1 | 2] * a;
            poolAmount = Math.max(poolAmount, a);
          }
          // Directional response carries the material's shape; the warm bounce anchors the lamps.
          daylight = 0.3 + 0.19 * poolAmount + 0.05 * THREE.MathUtils.smoothstep(py, 1.2, 3.2);
          for (const p of practicals) daylight += p.strength * Math.exp(-(sq((px - p.x) / p.rx) + sq((pz - p.z) / p.rz) + sq((py - p.y) / p.ry)));
        }
        const k = ((z * size[1] + y) * size[0] + x) * 4;
        for (let c = 0; c < 3; c += 1) data[k + c] = Math.min(255, Math.round(((rgb[c] ?? 0) / 3) * 255));
        data[k + 3] = Math.round(THREE.MathUtils.clamp(daylight, 0, 1) * 255);
      }
    }
  }
  return volume(data, size, min, span);
};
